import { Component, EventEmitter, HostListener, inject, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Helper } from "src/app/core/helpers/helper";
import { QuickCommerseService } from "../../services/quick-commerse.service";
import { BookingService } from "../../services/booking.service";
import { CommonService } from "../../services/common.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject, Subscription, switchMap } from "rxjs";
import { GoogleMapsService } from "../../services/google-map.service";
import { MapBoxService } from "../../services/mapbox.service";
import { HistoryService } from "../../services/history.service";

@Component({
  selector: "app-booking-status-change-confirmation-quickcommerse-modal",
  templateUrl: "./booking-status-change-confirmation-quickcommerse-modal.component.html",
  styleUrl: "./booking-status-change-confirmation-quickcommerse-modal.component.scss",
  standalone: false
})
export class BookingStatusChangeConfirmationQuickcommerseModalComponent {
  bookingDetails: any;
  countryDetails: any;

  completedAddressPredictions: Array<any> = [];
  completedAddressMapBoxSuggestions: Array<any> = [];
  cancelBookingReasonList: Array<any> = [];
  pickerList: Array<any> = [];

  isCancelBooking: boolean = false;

  selectedCompletedAddressMapSearchIndex: number = -1;

  completeBookingDetailsForm!: FormGroup;

  private completeAddressInputSubject: Subject<string> = new Subject<string>();
  private completeAddressInputSubscription!: Subscription;

  modalRef!: NgbModalRef;

  @Output() changeQcBookingStatusEvent = new EventEmitter();
  @ViewChild("template") template!: TemplateRef<any>;

  private quickCommerseService = inject(QuickCommerseService);
  private bookingService = inject(BookingService);
  private commonService = inject(CommonService);
  private googleMapsApiService = inject(GoogleMapsService);
  private mapBoxService = inject(MapBoxService);
  private historyService = inject(HistoryService);

  constructor(private modalService: NgbModal, public helper: Helper) {}

  /**
   * Opens the modal with booking details and initializes the form.
   * @param bookingDetails The details of the booking.
   * @param countryDetails The details of the country.
   * @param isCancelBooking Flag indicating if the booking is being canceled.
   */
  async openModal(bookingDetails: any, countryDetails: any, isCancelBooking: boolean): Promise<void> {
    this.bookingDetails = JSON.parse(JSON.stringify(bookingDetails));
    this.countryDetails = countryDetails;
    this.isCancelBooking = isCancelBooking;

    if (this.isCancelBooking) {
      await this.getCancellationReasonList();
    }

    this.initCompleteBookingDetailsForm();

    this.patchCompleteAddress();

    if (this.getQuickCommerceNextBookingStatus() === this.helper.BookingStatus.PICKER_ASSIGNED) {
      await this.findPicker();
    }

    if (this.getQuickCommerceNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION && !this.isCancelBooking) {
      this.initCompleteAddressAutoComplete();
    }

    this.modalRef = this.modalService.open(this.template, { centered: true, backdrop: "static" });
    this.modalRef.result.then(
      () => {},
      () => {}
    );
  }

  /**
   * Closes the modal.
   */
  closeModal(): void {
    this.modalRef?.dismiss("Cross click");
  }

  /**
   * Finds the picker for the booking.
   * @returns A promise that resolves when the picker is found.
   */
  findPicker(): Promise<void> {
    return new Promise((resolve) => {
      const parameters: any = { isShowErrorToast: false, merchantId: this.bookingDetails.quickCommerce.merchantDetail.id };
      this.bookingService.findPicker(parameters).subscribe((response: any) => {
        this.pickerList = response?.data?.pickers ?? [];
        resolve();
      });
    });
  }

  /**
   * Patches the complete address in the form.
   */
  patchCompleteAddress(): void {
    const destinationAddresses = JSON.parse(JSON.stringify(this.bookingDetails?.destinationAddresses.slice(-1)));

    const address: any = destinationAddresses.length > 0 ? destinationAddresses[0] : this.bookingDetails.address;

    if (address && (this.bookingDetails?.status === this.helper.BookingStatus.STARTED || this.bookingDetails?.status === this.helper.BookingStatus.ARRIVED_AT_STOP)) {
      this.completeBookingDetailsForm.patchValue({
        completeAddress: address.address,
        stopAddress: {
          address: address.address,
          latitude: address.latitude,
          longitude: address.longitude,
          city: address.city,
          countryCode: address.countryCode,
          country: address.country,
          postalCode: address.postalCode,
          note: "",
          placeId: address.placeId
        }
      });
    }
  }

  /**
   * Retrieves the cancellation reason list.
   * @returns A promise that resolves when the cancellation reason list is retrieved.
   */
  getCancellationReasonList(): Promise<void> {
    return new Promise((resolve) => {
      const parameter = { type: this.helper.UserType.ADMIN };
      this.commonService.getCancellationReasonList(parameter).subscribe((response: any) => {
        this.cancelBookingReasonList = response?.cancellationReasons?.length > 0 ? response.cancellationReasons : [];
        resolve();
      });
    });
  }

  /**
   * Initializes the complete booking details form.
   */
  initCompleteBookingDetailsForm(): void {
    this.completeBookingDetailsForm = new FormGroup({
      note: new FormControl<string | null>(null)
    });

    if (this.getQuickCommerceNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION && !this.isCancelBooking) {
      this.completeBookingDetailsForm.addControl("completeAddress", new FormControl<string | null>(null, [Validators.required]));
      this.completeBookingDetailsForm.addControl("stopAddress", new FormControl<any>(null, [Validators.required]));
    }

    if (this.isCancelBooking) {
      this.completeBookingDetailsForm.addControl("cancellationReason", new FormControl<string | null>(null, [Validators.required]));
      this.completeBookingDetailsForm.addControl("specificReason", new FormControl<string | null>(null));
    }

    if (this.getQuickCommerceNextBookingStatus() === this.helper.BookingStatus.PICKER_ASSIGNED) {
      this.completeBookingDetailsForm.addControl("pickerId", new FormControl<string | null>(null, [Validators.required]));
    }
  }

  /**
   * Gets the specific reason input control.
   * @returns The specific reason input control.
   */
  get specificReasonInput(): any {
    return this.completeBookingDetailsForm.get("specificReason");
  }

  /**
   * Gets the next booking status for QuickCommerce.
   * @returns The next booking status.
   */
  getQuickCommerceNextBookingStatus(): number {
    switch (this.bookingDetails?.quickCommerce?.status) {
      case this.helper.BookingStatus.REQUESTED:
        return this.helper.BookingStatus.MERCHANT_ACCEPTED;

      case this.helper.BookingStatus.MERCHANT_ACCEPTED:
      case this.helper.BookingStatus.NO_PICKER_FOUND:
        return this.helper.BookingStatus.PICKER_ASSIGNED;

      case this.helper.BookingStatus.ARRIVED_AT_PICKUP:
        return this.helper.BookingStatus.PICKED;

      case this.helper.BookingStatus.PICKED:
        return this.helper.BookingStatus.STARTED;

      case this.helper.BookingStatus.STARTED:
        return this.helper.BookingStatus.ARRIVED_AT_DESTINATION;

      default:
        return 0;
    }
  }

  /**
   * Gets the button string for the current QuickCommerce status.
   * @returns The button string.
   */
  getQuickCommerceStatusButtonString(): string {
    switch (this.bookingDetails?.quickCommerce?.status) {
      case this.helper.BookingStatus.REQUESTED:
        return this.helper.translateService.instant("label-title.merchant-accept");

      case this.helper.BookingStatus.MERCHANT_ACCEPTED:
      case this.helper.BookingStatus.NO_PICKER_FOUND:
        return this.helper.translateService.instant("label-title.assign-picker");

      case this.helper.BookingStatus.ARRIVED_AT_PICKUP:
        return this.helper.translateService.instant("label-title.picked");

      case this.helper.BookingStatus.PICKED:
        return this.helper.translateService.instant("label-title.start");

      case this.helper.BookingStatus.STARTED:
        return this.helper.translateService.instant("label-title.complete");

      default:
        return "";
    }
  }

  /**
   * Gets the cancel booking string.
   * @returns The cancel booking string.
   */
  getCancelBookingString(): void {
    return this.helper.translateService.instant("label-title.cancel");
  }

  /**
   * Initializes the autocomplete for the complete address.
   */
  initCompleteAddressAutoComplete(): void {
    const params = this.countryDetails.code2 ? { region: this.countryDetails.code2 } : {};
    if (this.completeAddressInputSubscription) {
      this.completeAddressInputSubscription.unsubscribe();
    }
    this.completeAddressInputSubscription = this.completeAddressInputSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(async (input) => {
          this.completedAddressPredictions = [];
          return await this.googleMapsApiService.getPlacePredictions(input, params);
        })
      )
      .subscribe((predictions) => {
        this.completedAddressPredictions = predictions;
      });
  }

  /**
   * Handles the input change event for the complete address.
   * @param event The input change event.
   */
  onCompleteAddressInputChange(event: Event): void {
    if (this.helper.mapTypeGoogle()) {
      const inputElement = event.target as HTMLInputElement;
      const inputValue = inputElement.value.trim();

      this.completeAddressInputSubject.next(inputValue);
    } else if (this.helper.mapTypeMapBox()) {
      this.debouncedOnCompletedAddressSearchMapbox(event);
    }
  }

  debouncedOnCompletedAddressSearchMapbox = this.helper.debounce(this.onSearch.bind(this), 500);

  /**
   * Handles the search event for MapBox.
   * @param event The search event.
   */
  onSearch(event: any): void {
    const query = event.target.value;
    if (query.length > 2) {
      const code: string = this.countryDetails.code ?? "";
      this.mapBoxService.search(query, code).subscribe((response: any) => {
        this.completedAddressMapBoxSuggestions = response.features;
      });
    } else {
      this.completedAddressMapBoxSuggestions = [];
    }
  }

  /**
   * Checks if the completed address is selected.
   * @param index The index of the address.
   * @returns True if the address is selected, false otherwise.
   */
  isCompletedAddressSelected(index: number): boolean {
    return this.selectedCompletedAddressMapSearchIndex === index;
  }

  /**
   * Handles the selection of a prediction.
   * @param prediction The selected prediction.
   */
  async onPredictionSelect(prediction: any): Promise<void> {
    this.completedAddressPredictions = [];
    const place = await this.googleMapsApiService.fetchFields(prediction);
    this.completeBookingDetailsForm.patchValue({
      completeAddress: place.formatted_address,
      stopAddress: this.extractAddressDetailsUsingGoogle(place, prediction)
    });
  }

  /**
   * Extracts address details using Google Maps.
   * @param place The place details.
   * @param prediction The prediction details.
   * @returns The extracted address details.
   */
  extractAddressDetailsUsingGoogle(place: any, prediction: any): any {
    const getAddressComponent = (types: string[]): any => place.address_components.find((component: any) => types.some((type) => component.types.includes(type)));

    const cityComponent = getAddressComponent(["locality", "administrative_area_level_3"]);
    const city = cityComponent?.longText ?? "";

    const countryComponent = getAddressComponent(["country"]);
    const country = countryComponent?.longText ?? "";
    const countryCode = countryComponent?.shortText ?? "";

    const postalCodeComponent = getAddressComponent(["postal_code"]);
    const postalCode = postalCodeComponent?.longText ?? "";

    return {
      address: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      city,
      countryCode,
      country,
      postalCode,
      note: "",
      placeId: prediction.place_id
    };
  }

  /**
   * Handles keyboard events for the document.
   * @param event The keyboard event.
   */
  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.helper.mapTypeGoogle()) {
      this.handleGoogleMapKeyboardEvent(event);
    } else if (this.helper.mapTypeMapBox()) {
      this.handleMapBoxKeyboardEvent(event);
    }
  }

  /**
   * Handles keyboard events for Google Maps.
   * @param event The keyboard event.
   */
  handleGoogleMapKeyboardEvent(event: KeyboardEvent): void {
    if (this.completedAddressPredictions.length > 0) {
      if (event.key === "ArrowDown") {
        this.selectedCompletedAddressMapSearchIndex = (this.selectedCompletedAddressMapSearchIndex + 1) % this.completedAddressPredictions.length;
      } else if (event.key === "ArrowUp") {
        this.selectedCompletedAddressMapSearchIndex =
          (this.selectedCompletedAddressMapSearchIndex - 1 + this.completedAddressPredictions.length) % this.completedAddressPredictions.length;
      } else if (
        event.key === "Enter" &&
        this.selectedCompletedAddressMapSearchIndex >= 0 &&
        this.selectedCompletedAddressMapSearchIndex < this.completedAddressPredictions.length
      ) {
        this.onPredictionSelect(this.completedAddressPredictions[this.selectedCompletedAddressMapSearchIndex]);
        this.selectedCompletedAddressMapSearchIndex = -1;
      }
    }
  }

  /**
   * Handles keyboard events for MapBox.
   * @param event The keyboard event.
   */
  handleMapBoxKeyboardEvent(event: KeyboardEvent): void {
    if (this.completedAddressMapBoxSuggestions.length > 0) {
      if (event.key === "ArrowDown") {
        this.selectedCompletedAddressMapSearchIndex = (this.selectedCompletedAddressMapSearchIndex + 1) % this.completedAddressMapBoxSuggestions.length;
      } else if (event.key === "ArrowUp") {
        this.selectedCompletedAddressMapSearchIndex =
          (this.selectedCompletedAddressMapSearchIndex - 1 + this.completedAddressMapBoxSuggestions.length) % this.completedAddressMapBoxSuggestions.length;
      } else if (
        event.key === "Enter" &&
        this.selectedCompletedAddressMapSearchIndex >= 0 &&
        this.selectedCompletedAddressMapSearchIndex < this.completedAddressMapBoxSuggestions.length
      ) {
        this.onSelect(this.completedAddressMapBoxSuggestions[this.selectedCompletedAddressMapSearchIndex]);
        this.selectedCompletedAddressMapSearchIndex = -1;
      }
    }
  }

  /**
   * Handles the selection of a MapBox suggestion.
   * @param suggestion The selected suggestion.
   */
  onSelect(suggestion: any): void {
    this.completeBookingDetailsForm.patchValue({
      completeAddress: suggestion.place_name,
      stopAddress: this.extractAddressDetailsFromMapBox(suggestion)
    });

    this.completedAddressMapBoxSuggestions = [];
  }

  /**
   * Extracts address details from MapBox.
   * @param suggestion The suggestion details.
   * @returns The extracted address details.
   */
  extractAddressDetailsFromMapBox(suggestion: any): any {
    const getContextItem = (type: string): any => suggestion.context.find((item: any) => item.id.startsWith(type));

    const cityContext = getContextItem("place");
    const city = cityContext?.text ?? "";

    const countryContext = getContextItem("country");
    const country = countryContext?.text ?? "";
    const countryCode = countryContext?.short_code.toUpperCase() ?? "";

    const postalCodeContext = getContextItem("postcode");
    const postalCode = postalCodeContext?.text ?? "";

    return {
      address: suggestion.place_name,
      latitude: suggestion.geometry.coordinates[1],
      longitude: suggestion.geometry.coordinates[0],
      city,
      countryCode,
      country,
      postalCode,
      note: "",
      placeId: suggestion.id
    };
  }

  /**
   * Starts the button loader.
   */
  startBtnLoader(): void {
    if (this.helper.showBtnLoader[1] === true) {
      return;
    }

    this.helper.showBtnLoader[1] = true;
  }

  /**
   * Updates the booking status.
   */
  updateBookingStatus(): void {
    if (this.getQuickCommerceNextBookingStatus() === this.helper.BookingStatus.MERCHANT_ACCEPTED) {
      this.acceptMerchantBooking();
      return;
    }

    if (this.getQuickCommerceNextBookingStatus() === this.helper.BookingStatus.PICKER_ASSIGNED) {
      this.reAssingPicker();
      return;
    }

    this.changeBookingStatus();
  }

  /**
   * Changes the booking status.
   */
  changeBookingStatus(): void {
    const parameters: any = { bookingId: this.bookingDetails._id, status: this.getQuickCommerceNextBookingStatus() };
    if (this.bookingDetails.confirmedDriver?.id) {
      parameters["driverId"] = this.bookingDetails.confirmedDriver.id;
    }

    if (this.getQuickCommerceNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION && !this.isCancelBooking) {
      if (this.completeBookingDetailsForm.invalid) {
        this.completeBookingDetailsForm.markAllAsTouched();
        return;
      }

      parameters["stopAddress"] = this.completeBookingDetailsForm.value.stopAddress;
    }

    parameters["note"] = this.completeBookingDetailsForm.value.note ?? "";

    this.startBtnLoader();

    this.quickCommerseService.changeBookingStatus(this.bookingDetails._id, parameters).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          this.bookingDetails.status = this.getQuickCommerceNextBookingStatus();
          this.changeQcBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Sets the validators for the specific reason input.
   */
  setSpecificReasonValidators(): void {
    if (this.completeBookingDetailsForm.value.cancellationReason === "Other") {
      this.completeBookingDetailsForm.get("specificReason")?.setValidators([Validators.required]);
    } else {
      this.completeBookingDetailsForm.get("specificReason")?.setValidators(null);
    }
    this.completeBookingDetailsForm.get("specificReason")?.updateValueAndValidity();
  }

  /**
   * Cancels the booking.
   */
  cancelBooking(): void {
    this.completeBookingDetailsForm.patchValue({
      specificReason: this.completeBookingDetailsForm.value.specificReason?.toString()?.trim()
    });

    this.setSpecificReasonValidators();

    if (this.completeBookingDetailsForm.invalid) {
      this.completeBookingDetailsForm.markAllAsTouched();
      return;
    }

    const parameters: any = {
      note: this.completeBookingDetailsForm.value.note ?? ""
    };

    if (this.completeBookingDetailsForm.value.cancellationReason === "Other") {
      parameters.cancellationReason = this.completeBookingDetailsForm.value.specificReason;
    } else {
      parameters.cancellationReason = this.completeBookingDetailsForm.value.cancellationReason;
    }

    this.startBtnLoader();

    this.historyService.cancelBooking(this.bookingDetails._id, parameters).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          this.bookingDetails.status = this.helper.BookingStatus.CANCELLED;
          this.changeQcBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Accepts the merchant booking.
   */
  acceptMerchantBooking(): void {
    this.startBtnLoader();

    this.quickCommerseService.acceptMerchantBooking(this.bookingDetails._id, { merchantId: this.bookingDetails.quickCommerce.merchantDetail.id }).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          this.bookingDetails.quickCommerce.status = this.helper.BookingStatus.MERCHANT_ACCEPTED;
          this.changeQcBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Reassigns the picker for the booking.
   */
  reAssingPicker(): void {
    if (this.completeBookingDetailsForm.invalid) {
      this.completeBookingDetailsForm.markAllAsTouched();
      return;
    }

    const parameters: any = { pickerId: this.completeBookingDetailsForm.value.pickerId, merchantId: this.bookingDetails.quickCommerce.merchantDetail.id };
    this.quickCommerseService.reAssingPicker(this.bookingDetails._id, parameters).subscribe((response: any) => {
      if (response) {
        this.bookingDetails.quickCommerce.status = this.helper.BookingStatus.PICKER_ASSIGNED;
        const picker = this.pickerList.find((p: any) => p._id === this.completeBookingDetailsForm.value.pickerId);
        this.bookingDetails.quickCommerce.confirmedPicker = {
          id: this.completeBookingDetailsForm.value.pickerId,
          imageUrl: picker.imageUrl,
          name: picker.name,
          email: picker.email,
          phone: picker.phone,
          uniqueId: picker.uniqueId
        };
        this.changeQcBookingStatusEvent.emit(this.bookingDetails);
        this.closeModal();
      }
    });
  }
}
